import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { linkToSignOut } from "$routes/_lib/links";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import { type FC, type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import {
  EPOCUS_PRIMARY,
  EPOCUS_PRIMARY_HOVER_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "./epocus-palette";
import { useCan } from "./rbac/use-can";
import {
  PROBE_ASSOCIATION_FAILURE_MESSAGES,
  PROBE_SERIAL_HINT,
  type ProbeAssociationFailure,
  useManageProbeAssociations,
  useMyProbeAssociations,
} from "./use-probe-associations";

const Title = styled(Typography)({
  fontSize: rem(20),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const Body = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
});

const Hint = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const Content = styled(DialogContent)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  paddingBottom: 24,
});

const SubmitButton = styled(Button)({
  minHeight: 44,
  borderRadius: 10,
  fontSize: rem(14),
  fontWeight: 600,
  textTransform: "none",
});

const SignOutLink = styled(Link)({
  alignSelf: "center",
  display: "flex",
  alignItems: "center",
  minHeight: 44,
  padding: "0 12px",
  fontSize: rem(13),
  fontWeight: 600,
  color: EPOCUS_PRIMARY,
  textDecoration: "none",
  borderRadius: 8,
  "&:hover": {
    backgroundColor: EPOCUS_PRIMARY_HOVER_BG,
  },
});

export const ProbeLinkRequiredDialog: FC = () => {
  const mustLinkProbe = useCan("claimTriageEncounter");
  const [serialRestrictedTriage, { loading: flagLoading }] = useFeatureFlag(
    featureFlags.epocus.serialRestrictedTriage,
  );
  const { associations, loading } = useMyProbeAssociations();
  const { add, pendingSerial } = useManageProbeAssociations();
  const [serialInput, setSerialInput] = useState("");
  const [failure, setFailure] = useState<ProbeAssociationFailure | null>(null);

  const open =
    mustLinkProbe &&
    Boolean(serialRestrictedTriage) &&
    !flagLoading &&
    !loading &&
    associations.length === 0;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFailure(await add({ serial: serialInput, aeTitle: "" }));
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      fullWidth
      maxWidth="xs"
      aria-labelledby="epocus-probe-link-required-title"
    >
      <DialogTitle disableTypography>
        <Title id="epocus-probe-link-required-title">Link your probe</Title>
      </DialogTitle>
      <Content>
        <Body>
          Before you can use ePOCUS, link the probe you scan with. Scans from
          your probe then appear in your To Document queue.
        </Body>
        <Hint>{PROBE_SERIAL_HINT}</Hint>
        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              margin="dense"
              label="Probe serial number"
              value={serialInput}
              onChange={(event) => {
                setSerialInput(event.target.value);
                setFailure(null);
              }}
              error={failure !== null}
              helperText={
                failure
                  ? PROBE_ASSOCIATION_FAILURE_MESSAGES[failure]
                  : undefined
              }
              inputProps={{ "aria-label": "Probe serial number" }}
            />
            <SubmitButton
              type="submit"
              variant="contained"
              color="primary"
              disableElevation
              disabled={
                pendingSerial !== null || serialInput.trim().length === 0
              }
            >
              {pendingSerial !== null ? (
                <CircularProgress size={16} />
              ) : (
                "Link probe"
              )}
            </SubmitButton>
          </Box>
        </form>
        <SignOutLink to={linkToSignOut()}>Log out</SignOutLink>
      </Content>
    </Dialog>
  );
};
